import axios from 'axios'
import { confirmOtpCode, sendOtpToPhone } from 'firebaseConfig'
import { t } from 'i18next'
import { baseURL } from 'models'
import { useState, useCallback } from 'react'
import { toast } from 'sonner'

export type OtpStep = 'enterPhone' | 'enterCode' | 'verified'

export type OtpErrorKey = 'sendFailed' | 'invalidCode' | 'requestError' | null

export const isLifecellNumber = (rawPhone: string) => {
  const lifecellPrefixes = ['38063', '38093', '38073']

  const digits = rawPhone.replace(/\D/g, '')

  return lifecellPrefixes.some(prefix => digits.startsWith(prefix))
}

export const sendOtpNumber = async (phone: string): Promise<boolean> => {
  const digits = phone.replace(/\D/g, '')
  const phoneE164 = `+${digits}`
  const isNumberLifecell = isLifecellNumber(digits)

  try {
    if (isNumberLifecell) {
      await axios.post(baseURL + '/api/user/signin-with-phone', {
        phone: phoneE164,
      })
      return true
    } else {
      const ok = await sendOtpToPhone(phoneE164)
      if (!ok) throw new Error('firebaseOtpFailed')
      return true
    }
  } catch (error) {
    console.error('sendOtpNumber error', error)
    toast.error(t('errorSendedCode'))
    return false
  }
}

export const confirmCode = async (
  code: string,
  phone: string,
): Promise<boolean> => {
  const digits = phone.replace(/\D/g, '')
  const phoneE164 = `+${digits}`
  const isNumberLifecell = isLifecellNumber(digits)

  try {
    if (isNumberLifecell) {
      await axios.post(baseURL + '/api/user/complete-phone-signin', {
        phone: phoneE164,
        code,
      })
      return true
    } else {
      const token = await confirmOtpCode(code)
      if (!token) throw new Error('firebaseConfirmFailed')
      return true
    }
  } catch (error) {
    console.error('confirmCode error', error)
    toast.error(t('errorCode'))
    return false
  }
}

type UsePhoneOtpOptions = {
  onVerified?: (phone: string) => void | Promise<void>
}

export const usePhoneOtp = (options?: UsePhoneOtpOptions) => {
  const [step, setStep] = useState<OtpStep>('enterPhone')
  const [isSending, setIsSending] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [errorKey, setErrorKey] = useState<OtpErrorKey>(null)

  const reset = useCallback(() => {
    setStep('enterPhone')
    setIsSending(false)
    setIsConfirming(false)
    setErrorKey(null)
  }, [])

  const send = useCallback(async (phone: string): Promise<boolean> => {
    if (!phone) return false

    setIsSending(true)
    setErrorKey(null)

    try {
      const ok = await sendOtpNumber(phone)

      if (!ok) {
        setErrorKey('sendFailed')
        return false
      }

      setStep('enterCode')
      return true
    } catch (e) {
      console.error('usePhoneOtp.send error', e)
      setErrorKey('requestError')
      return false
    } finally {
      setIsSending(false)
    }
  }, [])

  const confirm = useCallback(
    async (code: string, phone: string): Promise<boolean> => {
      if (!code || !phone) return false

      setIsConfirming(true)
      setErrorKey(null)

      try {
        const ok = await confirmCode(code, phone)

        if (!ok) {
          setErrorKey('invalidCode')
          return false
        }

        setStep('verified')

        if (options?.onVerified) {
          await options.onVerified(phone)
        }

        return true
      } catch (e) {
        console.error('usePhoneOtp.confirm error', e)
        setErrorKey('requestError')
        return false
      } finally {
        setIsConfirming(false)
      }
    },
    [options],
  )

  return {
    step,
    isSending,
    isConfirming,
    errorKey,
    send,
    confirm,
    reset,
  }
}
